import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, FileText } from "lucide-react";
import SectionHeading from "@/components/SectionHeading";
import { proposalCategories } from "@/data/content";

export default function Proposals() {
  const [activeTab, setActiveTab] = useState(proposalCategories[0].id);
  const [expanded, setExpanded] = useState(null);

  const active = proposalCategories.find((c) => c.id === activeTab);

  return (
    <section id="propostas" data-testid="proposals-section" className="relative overflow-hidden bg-white py-20 sm:py-28 lg:py-32">
      <div className="absolute -right-40 top-20 h-96 w-96 rounded-full bg-brand-light/15 blur-3xl" aria-hidden="true" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
        <SectionHeading
          chapter="03"
          kicker="Nossas propostas"
          title="Propostas construídas com a comunidade"
          description="Escolha um eixo e explore cada proposta. Tudo aqui nasce da escuta de quem vive e trabalha no Extremo Sul da Bahia."
        />

        <div className="mb-10 flex flex-wrap gap-2" role="tablist" aria-label="Eixos de propostas">
          {proposalCategories.map((c) => (
            <button
              key={c.id}
              type="button"
              role="tab"
              aria-selected={activeTab === c.id}
              data-testid={`proposal-tab-${c.id}`}
              onClick={() => { setActiveTab(c.id); setExpanded(null); }}
              className={`rounded-full px-5 py-2.5 text-sm font-bold transition-all duration-200 ${
                activeTab === c.id
                  ? "bg-brand-dark text-white shadow-lg"
                  : "bg-slate-100 text-slate-600 hover:bg-brand-royal/10 hover:text-brand-royal"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.35 }}
            className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3"
          >
            {active.items.map((item, i) => {
              const isOpen = expanded === i;
              return (
                <div
                  key={item.title}
                  data-testid={`proposal-card-${activeTab}-${i}`}
                  className="flex flex-col rounded-3xl border border-slate-200 bg-slate-50 p-7 transition-all duration-200 hover:border-brand-royal/40 hover:shadow-xl"
                >
                  <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-brand-orange/15 text-brand-orange">
                    <FileText className="h-5 w-5" />
                  </div>
                  <h3 className="font-display text-lg font-bold text-slate-900">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{item.summary}</p>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.p
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden text-sm leading-relaxed text-slate-600"
                        data-testid={`proposal-detail-${activeTab}-${i}`}
                      >
                        <span className="mt-3 block border-l-2 border-brand-orange pl-3">{item.detail}</span>
                      </motion.p>
                    )}
                  </AnimatePresence>
                  <button
                    type="button"
                    data-testid={`proposal-expand-${activeTab}-${i}`}
                    onClick={() => setExpanded(isOpen ? null : i)}
                    className="mt-4 inline-flex items-center gap-1 self-start text-sm font-bold text-brand-royal transition-colors hover:text-brand-orange"
                    aria-expanded={isOpen}
                  >
                    {isOpen ? "Recolher" : "Ver detalhes"}
                    <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
                  </button>
                </div>
              );
            })}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}
